/**
 * The rail: the customer chrome on every signed-in surface. It is a tree, top
 * to bottom: the workspace, the reader's inbox, the Teams, then the rest.
 *
 *   1. The workspace switcher. Its menu holds Settings, Invite people, and
 *      the other workspaces the reader belongs to.
 *   2. Waiting on you: one inbox across every Team, counting the gates
 *      addressed to the reader. It turns blue when it holds anything. Search
 *      sits quiet under it and opens the ⌘K palette.
 *   3. Teams: one row per Team with its mark in the Team's colour, the name
 *      on up to two lines, and one count: the gates waiting on the reader,
 *      highlighted, when there are any; otherwise how many tickets its queue
 *      has Working, quiet; otherwise nothing. All teams sits under them and
 *      Add a team is a visible dashed row.
 *   4. Workspace, low and quiet: Pull request reviews (counting the reviews
 *      Proctor holds for a person), Decisions, Connections, Usage (once there
 *      is usage to show), Settings. Workspace-wide and visited less than the
 *      Teams.
 *   5. The account: appearance, Your settings (the You section of Settings),
 *      YAGNI Code, help, Super admin for YAGNI staff, and sign out.
 *
 * When the sheet is wide the rail folds to a 56px strip of glyphs. On a phone
 * the same tree is a drawer from the left, opened from a sticky top bar that
 * keeps the workspace and the Waiting on you count in view (styles/queue.css).
 * The drawer folds on a tap outside it, on Escape, and on every navigation.
 * Every way out of the rail (a link, the phone bar's inbox, a menu item) also
 * folds an open Ask dock, so the page it leads to is never left under the chat.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { PRODUCT_DESTINATIONS, can, type AuthSessionResponse } from "@yagni/shared";
import { trackClientEvent } from "../analytics/index.js";
import { AUTHENTICATED_DEFAULT_PATH } from "../routes/defaults.js";
import { resolveFeatureFlag } from "../hooks/useFeatureFlag.js";
import { useTheme } from "../hooks/useTheme.js";
import { THEME_PREFERENCES, THEME_PREFERENCE_LABELS } from "../lib/theme.js";
import { TeamIcon, defaultTeamIcon } from "../ui/team-icon.js";
import { deskAccentToken } from "../desks/deskAccent.js";
import { useUsageSummary } from "../usage/usageReport.js";
import { useDecisionsSummary } from "../ledger/useDecisionsSummary.js";
import { useHeldReviewCount } from "../reviews/useReviewsList.js";
import { useConnectorAlerts } from "../hooks/useConnectorAlerts.js";
import { MenuItem, MenuLabel, MenuSeparator, MoreMenu } from "../queue/MoreMenu.js";
import { ChevronDownIcon, CloseIcon, MenuIcon } from "../queue/icons.js";
import { cx } from "../queue/primitives.js";
import { useNavDesks } from "./useNavDesks.js";
import { useTeamWorking } from "./liveTeams.js";
import { askDockStore } from "./askDockStore.js";
import { searchStore } from "./searchStore.js";
import { initialsOf } from "../lib/names.js";

export interface RailProps {
  session: AuthSessionResponse;
  onSignOut: () => void;
  /** Opens the Help & feedback panel (App owns the panel state). */
  onOpenFeedback?: () => void;
}

export { initialsOf };

function Count({ n, title, quiet }: { n: number; title?: string; quiet?: boolean }) {
  return (
    <span
      className={cx("count", n === 0 && "zero", quiet && "quiet")}
      title={title}
      aria-label={title}
    >
      {n}
    </span>
  );
}

function under(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function Rail({ session, onSignOut, onOpenFeedback }: RailProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // A way out of the rail folds an open Ask dock, which would otherwise cover the page it leads to.
  const go = (path: string) => {
    askDockStore.setOpen(false);
    navigate(path);
  };
  const { desks, isLoading } = useNavDesks(session.user.id);
  const teamWorking = useTeamWorking();
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The drawer is a way somewhere: arriving folds it.
  useEffect(() => setDrawerOpen(false), [pathname]);

  const menuButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLElement>(null);

  // Open, the drawer is modal: focus moves in, the page behind is inert and
  // does not scroll, and closing hands focus back to the menu button.
  useEffect(() => {
    const aside = drawer.current;
    if (!drawerOpen || !aside) return;
    const behind = Array.from(aside.parentElement?.children ?? []).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement &&
        el !== aside &&
        !el.classList.contains("rail-scrim") &&
        !el.hasAttribute("inert"),
    );
    for (const el of behind) el.setAttribute("inert", "");
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    return () => {
      for (const el of behind) el.removeAttribute("inert");
      document.body.style.overflow = overflow;
      const active = document.activeElement;
      if (!active || active === document.body || aside.contains(active))
        menuButton.current?.focus();
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  const workspaceRole =
    (session.memberships ?? []).find((m) => m.workspace.id === session.workspace?.id)?.role ??
    "member";
  const isWorkspaceAdmin = workspaceRole === "admin" || workspaceRole === "owner";
  const canAddTeam = can(workspaceRole, "create-team");

  // Month-to-date usage, scoped to the viewer: admins read the workspace
  // rollup, members their own line. No data hides the row.
  const usageSummary = useUsageSummary(isWorkspaceAdmin ? "workspace" : "me");
  const showUsage = usageSummary !== null && usageSummary.dispatches > 0;
  // The ledger's needs-you count: unconfirmed decisions the agents already cite.
  const decisionsNeedYou = useDecisionsSummary()?.leanedOn ?? 0;
  // Pull request reviews Proctor holds for a person, over the Teams the reader sees.
  const heldReviews = useHeldReviewCount();
  // Connectors whose sync stalled or failed.
  const connectorAlerts = useConnectorAlerts().alerts.length;

  // Gates ADDRESSED to this reader, summed over the Teams they can see.
  const waitingOnYou = useMemo(
    () => desks.reduce((sum, d) => sum + (d.needsYouForViewer ?? 0), 0),
    [desks],
  );

  // A hard redirect (not a client-side navigate), because every mounted
  // surface is workspace-scoped. A failed POST still reloads, landing the
  // reader back in the current workspace, which is the safe outcome.
  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      trackClientEvent("Workspace Switched", {
        from_workspace_id: session.workspace.id,
        to_workspace_id: workspaceId,
        via: "workspace_menu",
      });
      try {
        await fetch("/api/auth/switch-workspace", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ workspaceId }),
        });
      } catch (err) {
        console.error("Workspace switch failed:", err);
      } finally {
        window.location.href = AUTHENTICATED_DEFAULT_PATH;
      }
    },
    [session.workspace.id],
  );

  const workspaceName = session.workspace.name || "Workspace";
  const memberships = session.memberships ?? [];
  const displayName = session.user.displayName ?? session.user.email ?? "Account";
  const showFeedback = !!onOpenFeedback && resolveFeatureFlag(session, "feedback.panel.enabled");
  const showYagniCode = resolveFeatureFlag(session, "yagni_code.enabled");

  const waitingOn = under(pathname, PRODUCT_DESTINATIONS.waiting.path);

  return (
    <>
      <header className="railbar" data-testid="rail-bar">
        <button
          type="button"
          ref={menuButton}
          className="menubtn"
          aria-label="Open navigation"
          aria-controls="app-rail"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
          data-testid="rail-bar-menu"
        >
          <MenuIcon />
        </button>
        <span className="mark" aria-hidden>
          {workspaceName.trim()[0]?.toUpperCase() ?? "W"}
        </span>
        <span className="txt">{workspaceName}</span>
        <Link
          to={PRODUCT_DESTINATIONS.waiting.path}
          className={cx("inbox", waitingOnYou > 0 && "some", waitingOn && "on")}
          aria-label={`Waiting on you: ${waitingOnYou}`}
          aria-current={waitingOn ? "page" : undefined}
          onClick={() => askDockStore.setOpen(false)}
          data-testid="rail-bar-inbox"
        >
          <span className="gl" aria-hidden>
            ●
          </span>
          {waitingOnYou}
        </Link>
      </header>
      {drawerOpen && (
        <div
          className="rail-scrim"
          aria-hidden
          onClick={() => setDrawerOpen(false)}
          data-testid="rail-scrim"
        />
      )}
      <aside
        ref={drawer}
        id="app-rail"
        className={cx("rail", drawerOpen && "drawn")}
        data-testid="app-rail"
        aria-label="Workspace navigation"
        onClick={(event) => {
          // A link to the page already shown changes no path, so it folds the drawer itself,
          // and an open Ask dock with it.
          if (event.target instanceof Element && event.target.closest("a")) {
            setDrawerOpen(false);
            askDockStore.setOpen(false);
          }
        }}
      >
        <button
          type="button"
          ref={closeButton}
          className="drawerclose"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
        >
          <CloseIcon />
        </button>
        <MoreMenu
          align="left"
          className="wsw rail-menu"
          label="Workspace"
          trigger={({ open, toggle }) => (
            <button
              type="button"
              className="nav wsbtn"
              aria-label="Workspace menu"
              aria-haspopup="menu"
              aria-expanded={open}
              title={workspaceName}
              onClick={toggle}
            >
              <span className="mark" aria-hidden>
                {workspaceName.trim()[0]?.toUpperCase() ?? "W"}
              </span>
              <span className="txt">{workspaceName}</span>
              <ChevronDownIcon />
            </button>
          )}
        >
          <MenuItem onSelect={() => go(PRODUCT_DESTINATIONS.settings.path)}>
            {PRODUCT_DESTINATIONS.settings.navLabel}
          </MenuItem>
          <MenuItem onSelect={() => go("/settings/people")}>Invite people</MenuItem>
          <MenuSeparator />
          <MenuLabel>Switch workspace</MenuLabel>
          {memberships.length > 0 ? (
            memberships.map((m) => {
              const current = m.workspace.id === session.workspace.id;
              return (
                <MenuItem
                  key={m.workspace.id}
                  on={current}
                  hint={current ? "current" : undefined}
                  onSelect={current ? undefined : () => void switchWorkspace(m.workspace.id)}
                >
                  {m.workspace.name}
                </MenuItem>
              );
            })
          ) : (
            <MenuItem on hint="current">
              {workspaceName}
            </MenuItem>
          )}
        </MoreMenu>

        <nav className="inboxnav" aria-label="Inbox">
          <Link
            to={PRODUCT_DESTINATIONS.waiting.path}
            className={cx("nav inbox", under(pathname, PRODUCT_DESTINATIONS.waiting.path) && "on")}
            aria-current={under(pathname, PRODUCT_DESTINATIONS.waiting.path) ? "page" : undefined}
            data-testid="rail-inbox"
            title="Waiting on you"
          >
            <span className="gl you" aria-hidden>
              ●
            </span>
            <span className="txt">Waiting on you</span>
            <Count n={waitingOnYou} title={`${waitingOnYou} waiting on you`} />
          </Link>
          <button
            type="button"
            className="nav search"
            onClick={() => {
              setDrawerOpen(false);
              searchStore.open();
            }}
            aria-keyshortcuts="Meta+K Control+K"
            data-testid="rail-search"
            title="Search every Team (⌘K)"
          >
            <span className="gl" aria-hidden>
              <SearchIcon width={12} height={12} />
            </span>
            <span className="txt">Search</span>
            <span className="kbd" aria-hidden>
              ⌘K
            </span>
          </button>
        </nav>

        <div>
          <div className="label">
            <span>Teams</span>
          </div>
          <nav className="teamnav" aria-label="Teams">
            {desks.map((desk) => {
              const to = `/desks/${desk.id}`;
              const active = under(pathname, to);
              const n = desk.needsYouForViewer ?? 0;
              const working = teamWorking.get(desk.id) ?? 0;
              return (
                <Link
                  key={desk.id}
                  to={to}
                  className={cx("nav", active && "on")}
                  aria-current={active ? "page" : undefined}
                  data-testid="rail-team"
                  title={desk.name}
                >
                  <span
                    className="tmark"
                    style={{ "--tc": deskAccentToken(desk.id) } as CSSProperties}
                    aria-hidden
                  >
                    <TeamIcon
                      name={defaultTeamIcon(desk)}
                      size={13}
                      style={{ opacity: 1, strokeWidth: 1.9 }}
                    />
                  </span>
                  <span className="txt">{desk.name}</span>
                  {/* One count: what needs the reader, highlighted; else what is Working, quiet. */}
                  {n > 0 ? (
                    <Count n={n} title={`${n} need you`} />
                  ) : working > 0 ? (
                    <Count n={working} title={`${working} working`} quiet />
                  ) : null}
                </Link>
              );
            })}
            <Link
              to={PRODUCT_DESTINATIONS.teams.path}
              className={cx(
                "nav allteams",
                under(pathname, PRODUCT_DESTINATIONS.teams.path) && "on",
              )}
              aria-current={under(pathname, PRODUCT_DESTINATIONS.teams.path) ? "page" : undefined}
            >
              <span className="gl" aria-hidden>
                A
              </span>
              <span className="txt">All teams</span>
            </Link>
            {canAddTeam && !isLoading && (
              <Link
                to="/desks/new"
                className={cx("nav addteam", pathname === "/desks/new" && "on")}
                data-testid="rail-add-team"
                title="Add a team"
              >
                <span className="gl" aria-hidden>
                  +
                </span>
                <span className="txt">Add a team</span>
              </Link>
            )}
          </nav>
        </div>

        <div className="foot">
          <div className="wsgroup">
            <div className="label">
              <span>Workspace</span>
            </div>
            <nav aria-label="Workspace">
              <WorkspaceRow
                to={PRODUCT_DESTINATIONS.reviews.path}
                glyph="PR"
                label={PRODUCT_DESTINATIONS.reviews.navLabel}
                count={heldReviews}
                countTitle={`${heldReviews} held for you`}
                pathname={pathname}
              />
              <WorkspaceRow
                to={PRODUCT_DESTINATIONS.decisions.path}
                glyph="D"
                label={PRODUCT_DESTINATIONS.decisions.navLabel}
                count={decisionsNeedYou}
                countTitle={`${decisionsNeedYou} leaned on, not yet confirmed`}
                pathname={pathname}
              />
              <WorkspaceRow
                to={PRODUCT_DESTINATIONS.connections.path}
                glyph="C"
                label={PRODUCT_DESTINATIONS.connections.navLabel}
                count={connectorAlerts}
                countTitle={`${connectorAlerts} need attention`}
                pathname={pathname}
              />
              {showUsage && (
                <WorkspaceRow
                  to={PRODUCT_DESTINATIONS.usage.path}
                  glyph="U"
                  label={PRODUCT_DESTINATIONS.usage.navLabel}
                  pathname={pathname}
                />
              )}
              <WorkspaceRow
                to={PRODUCT_DESTINATIONS.settings.path}
                glyph="S"
                label={PRODUCT_DESTINATIONS.settings.navLabel}
                pathname={pathname}
              />
            </nav>
          </div>

          <MoreMenu
            align="left"
            direction="up"
            className="acct rail-menu"
            label="Account"
            trigger={({ open, toggle }) => (
              <button
                type="button"
                className="nav acctbtn"
                aria-label={`Account menu for ${displayName}`}
                aria-haspopup="menu"
                aria-expanded={open}
                title={displayName}
                onClick={toggle}
                data-testid="rail-account"
              >
                <span className="av" aria-hidden>
                  {initialsOf(session.user.displayName, session.user.email)}
                </span>
                <span className="txt">
                  {displayName}
                  <span className="sub">{workspaceRole}</span>
                </span>
              </button>
            )}
          >
            <MenuLabel>Appearance</MenuLabel>
            <div className="seg" data-keep-open role="radiogroup" aria-label="Appearance">
              {THEME_PREFERENCES.map((preference) => (
                <button
                  key={preference}
                  type="button"
                  role="radio"
                  aria-checked={theme.preference === preference}
                  className={theme.preference === preference ? "on" : undefined}
                  onClick={() => theme.setPreference(preference)}
                >
                  {THEME_PREFERENCE_LABELS[preference]}
                </button>
              ))}
            </div>
            <MenuSeparator />
            <MenuItem onSelect={() => go("/settings/you")}>Your settings</MenuItem>
            {/* The install lives on You; this is only the way there, gated on
              the workspace's YAGNI Code flag. */}
            {showYagniCode && (
              <MenuItem onSelect={() => go("/settings/you")}>Get YAGNI Code</MenuItem>
            )}
            {showFeedback && <MenuItem onSelect={onOpenFeedback}>Help &amp; feedback</MenuItem>}
            {session.user.isSuperadmin && (
              <>
                <MenuSeparator />
                <MenuItem hint="YAGNI staff" onSelect={() => go("/admin")}>
                  Super admin
                </MenuItem>
              </>
            )}
            <MenuSeparator />
            <MenuItem onSelect={onSignOut}>Sign out</MenuItem>
          </MoreMenu>
        </div>
      </aside>
    </>
  );
}

function WorkspaceRow({
  to,
  glyph,
  label,
  count,
  countTitle,
  pathname,
}: {
  to: string;
  glyph: string;
  label: string;
  count?: number;
  countTitle?: string;
  pathname: string;
}) {
  const active = under(pathname, to);
  return (
    <Link
      to={to}
      className={cx("nav", active && "on")}
      aria-current={active ? "page" : undefined}
      title={label}
    >
      <span className="gl" aria-hidden>
        {glyph}
      </span>
      <span className="txt">{label}</span>
      {count !== undefined && <Count n={count} title={countTitle} />}
    </Link>
  );
}
